import "@/App.css";
import React from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ErrorBoundary from "@/components/ErrorBoundary";
import StickyQuoteBar from "@/components/StickyQuoteBar";
import ScrollProgress from "@/components/ScrollProgress";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsent from "@/components/CookieConsent";
import SalesIQController from "@/components/SalesIQController";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import LeadPopup from "@/components/LeadPopup";
import AppRouter from "@/components/AppRouter";
import useGlobalTracking from "@/hooks/useGlobalTracking";

// PPC/event landing pages (e.g. /landing/partner-programme, /partner-with-us) ship their own
// minimal header/footer and must NOT render the global Navbar/Footer, the
// sticky quote bar, or the lead/exit popup. Everything else (ScrollToTop,
// ScrollProgress, CookieConsent, SalesIQ) stays site-wide.
function isBareChromeRoute(pathname) {
  // Normalise trailing slashes so prerendered /partner-with-us/ matches too.
  const path = pathname.replace(/\/+$/, "") || "/";
  return path.startsWith("/landing/") || path === "/partner-with-us";
}

function AppShell() {
  const { pathname } = useLocation();
  useGlobalTracking();
  const bare = isBareChromeRoute(pathname);
  return (
    <div className="App font-body min-h-screen flex flex-col">
      <ScrollProgress />
      <ScrollToTop />
      {!bare && <Navbar />}
      <main className="flex-1">
        <AppRouter />
      </main>
      {!bare && <Footer />}
      {!bare && <StickyQuoteBar />}
      {!bare && <WhatsAppFloat />}
      {!bare && <LeadPopup />}
      <CookieConsent />
      <SalesIQController />
    </div>
  );
}

function App() {
  // White-label: hide external platform branding badges
  React.useEffect(() => {
    const hide = () => {
      document.querySelectorAll('a[href*="emergentagent"]').forEach((el) => {
        const target = el.closest('div') || el;
        target.style.cssText = 'display:none!important;visibility:hidden!important;height:0!important;opacity:0!important';
      });
    };
    hide();
    // Throttled observer — only fires once per 500ms batch
    let pending = false;
    const observer = new MutationObserver(() => {
      if (pending) return;
      pending = true;
      setTimeout(() => { hide(); pending = false; }, 500);
    });
    observer.observe(document.body, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, []);  

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <AppShell />
        </ErrorBoundary>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
